import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { TID } from "@/testIds";
import { Phone, Lock, ArrowRight, Award, Info } from "lucide-react";

export default function LoginPage() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (user) return <Navigate to="/dashboard" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!phone.trim() || !password) {
      setError("Please enter phone and password");
      return;
    }
    setSubmitting(true);
    try {
      await login(phone.trim(), password);
      nav("/dashboard", { replace: true });
    } catch (err) {
      setError(err.message || "Login failed");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-white">
      {/* Hero */}
      <div
        className="relative md:w-1/2 min-h-[280px] md:min-h-screen flex items-end p-8 md:p-12 text-white overflow-hidden"
        style={{
          background:
            "linear-gradient(135deg, rgba(10,37,64,0.55) 0%, rgba(10,37,64,0.9) 60%, #0A2540 100%)",
        }}
      >
        <div
          className="absolute inset-0 opacity-30"
          style={{
            backgroundImage:
              "url('https://images.pexels.com/photos/37145697/pexels-photo-37145697.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            mixBlendMode: "overlay",
          }}
        />
        <div className="relative z-10 max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center">
              <Award className="w-5 h-5 text-[#0A2540]" />
            </div>
            <span className="font-bold tracking-wide">Pathaans Academy</span>
          </div>
          <h1 className="text-3xl md:text-5xl font-extrabold leading-tight">
            Pathaans Academy
            <br />
            Ludhiana
          </h1>
          <p className="mt-4 text-white/85 text-base md:text-lg">
            Manage students, attendance and renewals — all in one place.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="md:w-1/2 flex items-center justify-center p-6 md:p-12">
        <form
          onSubmit={submit}
          className="w-full max-w-md space-y-5"
          noValidate
        >
          <div>
            <h2 className="text-2xl font-bold text-slate-900">Sign in</h2>
            <p className="text-slate-500 text-sm mt-1">
              Use your phone number and password
            </p>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">
              Phone Number
            </label>
            <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 h-12 bg-slate-50 focus-within:border-[#0A2540] focus-within:bg-white">
              <Phone className="w-4 h-4 text-slate-400" />
              <input
                data-testid={TID.loginPhone}
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+91 99999 99999"
                className="flex-1 bg-transparent outline-none text-slate-900 placeholder:text-slate-400"
              />
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-600 mb-1.5 uppercase tracking-wider">
              Password
            </label>
            <div className="flex items-center gap-2 border border-slate-200 rounded-lg px-3 h-12 bg-slate-50 focus-within:border-[#0A2540] focus-within:bg-white">
              <Lock className="w-4 h-4 text-slate-400" />
              <input
                data-testid={TID.loginPassword}
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                className="flex-1 bg-transparent outline-none text-slate-900"
              />
            </div>
          </div>

          {error && (
            <p data-testid={TID.loginError} className="text-[#FF3B30] text-sm">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid={TID.loginSubmit}
            disabled={submitting}
            className="w-full h-12 bg-[#0A2540] hover:bg-[#081D33] disabled:opacity-60 text-white rounded-lg font-semibold flex items-center justify-center gap-2 transition-colors"
          >
            {submitting ? (
              <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                Sign In
                <ArrowRight className="w-4 h-4" />
              </>
            )}
          </button>

          <div className="flex items-start gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3">
            <Info className="w-4 h-4 text-slate-400 mt-0.5 shrink-0" />
            <p className="text-xs text-slate-600 leading-relaxed">
              Only owners and coaches can log in. Ask your admin to create a coach
              account.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
}
